import {
	Hct,
	hexFromArgb,
	SchemeContent,
	SchemeExpressive,
	SchemeFidelity,
	SchemeMonochrome,
	SchemeNeutral,
	SchemeTonalSpot,
	SchemeVibrant,
} from "@material/material-color-utilities";

export type ThemeMode = "light" | "dark" | "system";

/**
 * Color scheme variants from Material Design 3.
 * - `expressive` — MD3 Expressive 2025: high-chroma, intentionally detached from
 *   source color. **Default for this library.**
 * - `tonal_spot` — Standard Android 12+ theme: low-to-medium colorfulness.
 * - `vibrant` — More colorful than tonal_spot, hue from source.
 * - `fidelity` — Stays close to the source color's hue and chroma.
 * - `content` — Similar to fidelity, optimised for image-based content.
 * - `monochrome` — No hue; all grays.
 * - `neutral` — Near-neutral colors with subtle tint.
 */
export type SchemeVariant =
	| "expressive"
	| "tonal_spot"
	| "vibrant"
	| "fidelity"
	| "content"
	| "monochrome"
	| "neutral";

/**
 * Contrast level for the generated scheme.
 * - `-1` — Minimum contrast (accessibility not guaranteed)
 * - `0`  — Standard (design as spec'd)
 * - `0.5` — Medium contrast
 * - `1`  — Maximum contrast (WCAG AAA)
 */
export type ContrastLevel = -1 | 0 | 0.5 | 1;

/**
 * Resolves the effective color scheme from a ThemeMode.
 * When mode is "system", reads the OS preference via matchMedia.
 * Returns "light" as the safe default in SSR environments.
 */
export function resolveMode(mode: ThemeMode): "light" | "dark" {
	if (mode !== "system") return mode;
	if (typeof window === "undefined") return "light";
	return window.matchMedia("(prefers-color-scheme: dark)").matches
		? "dark"
		: "light";
}

// ─── Color Scheme Interface ────────────────────────────────────────────────────

export interface MD3ColorScheme {
	primary: string;
	onPrimary: string;
	primaryContainer: string;
	onPrimaryContainer: string;
	inversePrimary: string;
	primaryFixed: string;
	primaryFixedDim: string;
	onPrimaryFixed: string;
	onPrimaryFixedVariant: string;
	/** Expressive dim tone for primary (v0.4.0+) */
	primaryDim: string;

	secondary: string;
	onSecondary: string;
	secondaryContainer: string;
	onSecondaryContainer: string;
	secondaryFixed: string;
	secondaryFixedDim: string;
	onSecondaryFixed: string;
	onSecondaryFixedVariant: string;
	/** Expressive dim tone for secondary (v0.4.0+) */
	secondaryDim: string;

	tertiary: string;
	onTertiary: string;
	tertiaryContainer: string;
	onTertiaryContainer: string;
	tertiaryFixed: string;
	tertiaryFixedDim: string;
	onTertiaryFixed: string;
	onTertiaryFixedVariant: string;
	/** Expressive dim tone for tertiary (v0.4.0+) */
	tertiaryDim: string;

	error: string;
	onError: string;
	errorContainer: string;
	onErrorContainer: string;

	surface: string;
	onSurface: string;
	surfaceVariant: string;
	onSurfaceVariant: string;
	surfaceTint: string;
	/** Dimmed surface — darker than surface, lighter than surface-container. */
	surfaceDim: string;
	/** Brightened surface — lighter than surface. */
	surfaceBright: string;
	surfaceContainerLowest: string;
	surfaceContainerLow: string;
	surfaceContainer: string;
	surfaceContainerHigh: string;
	surfaceContainerHighest: string;

	inverseSurface: string;
	inverseOnSurface: string;

	background: string;
	onBackground: string;

	outline: string;
	outlineVariant: string;

	shadow: string;
	scrim: string;
}

// ─── Internal scheme factory ───────────────────────────────────────────────────

/**
 * Creates a `DynamicScheme` subclass instance from a seed hex color using
 * Material Color Utilities v0.4.0. Defaults to Spec 2025 + Expressive variant.
 */
function createDynamicScheme(
	sourceColorHex: string,
	isDark: boolean,
	variant: SchemeVariant = "expressive",
	contrastLevel: ContrastLevel = 0,
) {
	const hct = Hct.fromInt(argbFromHex(sourceColorHex));
	const spec = "2025" as const;

	switch (variant) {
		case "tonal_spot":
			return new SchemeTonalSpot(hct, isDark, contrastLevel, spec);
		case "vibrant":
			return new SchemeVibrant(hct, isDark, contrastLevel, spec);
		case "fidelity":
			return new SchemeFidelity(hct, isDark, contrastLevel, spec);
		case "content":
			return new SchemeContent(hct, isDark, contrastLevel, spec);
		case "monochrome":
			return new SchemeMonochrome(hct, isDark, contrastLevel, spec);
		case "neutral":
			return new SchemeNeutral(hct, isDark, contrastLevel, spec);
		default:
			return new SchemeExpressive(hct, isDark, contrastLevel, spec);
	}
}

/** Parse a hex string (#rrggbb) to ARGB int. */
function argbFromHex(hex: string): number {
	const sanitized = hex.replace(/^#/, "");
	const r = Number.parseInt(sanitized.substring(0, 2), 16);
	const g = Number.parseInt(sanitized.substring(2, 4), 16);
	const b = Number.parseInt(sanitized.substring(4, 6), 16);
	return (0xff000000 | (r << 16) | (g << 8) | b) >>> 0;
}

// ─── Public API ───────────────────────────────────────────────────────────────

export interface GenerateM3ThemeOptions {
	variant?: SchemeVariant;
	contrastLevel?: ContrastLevel;
}

/**
 * Generate a complete MD3 Expressive color scheme from a seed hex string.
 *
 * Uses Material Color Utilities v0.4.0 with SpecVersion 2025 by default.
 *
 * @param sourceColorHex - Seed color in hex format (e.g. `"#6750A4"`).
 * @param mode - Light or dark scheme. Defaults to `"light"`.
 * @param options - Optional variant + contrast overrides.
 *   - `variant` — defaults to `"expressive"` (MD3 Expressive 2025)
 *   - `contrastLevel` — defaults to `0` (standard)
 *
 * @example
 * // Backward-compatible call (no options required)
 * generateM3Theme("#6750A4")
 *
 * @example
 * // Expressive with high contrast
 * generateM3Theme("#6750A4", "dark", { contrastLevel: 1 })
 */
export function generateM3Theme(
	sourceColorHex: string,
	mode: "light" | "dark" = "light",
	options: GenerateM3ThemeOptions = {},
): MD3ColorScheme {
	const { variant = "expressive", contrastLevel = 0 } = options;
	const isDark = mode === "dark";
	const scheme = createDynamicScheme(
		sourceColorHex,
		isDark,
		variant,
		contrastLevel,
	);

	const hex = (argb: number) => hexFromArgb(argb);

	return {
		// Primary
		primary: hex(scheme.primary),
		onPrimary: hex(scheme.onPrimary),
		primaryContainer: hex(scheme.primaryContainer),
		onPrimaryContainer: hex(scheme.onPrimaryContainer),
		inversePrimary: hex(scheme.inversePrimary),
		primaryFixed: hex(scheme.primaryFixed),
		primaryFixedDim: hex(scheme.primaryFixedDim),
		onPrimaryFixed: hex(scheme.onPrimaryFixed),
		onPrimaryFixedVariant: hex(scheme.onPrimaryFixedVariant),
		primaryDim: hex(scheme.primaryDim),

		// Secondary
		secondary: hex(scheme.secondary),
		onSecondary: hex(scheme.onSecondary),
		secondaryContainer: hex(scheme.secondaryContainer),
		onSecondaryContainer: hex(scheme.onSecondaryContainer),
		secondaryFixed: hex(scheme.secondaryFixed),
		secondaryFixedDim: hex(scheme.secondaryFixedDim),
		onSecondaryFixed: hex(scheme.onSecondaryFixed),
		onSecondaryFixedVariant: hex(scheme.onSecondaryFixedVariant),
		secondaryDim: hex(scheme.secondaryDim),

		// Tertiary
		tertiary: hex(scheme.tertiary),
		onTertiary: hex(scheme.onTertiary),
		tertiaryContainer: hex(scheme.tertiaryContainer),
		onTertiaryContainer: hex(scheme.onTertiaryContainer),
		tertiaryFixed: hex(scheme.tertiaryFixed),
		tertiaryFixedDim: hex(scheme.tertiaryFixedDim),
		onTertiaryFixed: hex(scheme.onTertiaryFixed),
		onTertiaryFixedVariant: hex(scheme.onTertiaryFixedVariant),
		tertiaryDim: hex(scheme.tertiaryDim),

		// Error
		error: hex(scheme.error),
		onError: hex(scheme.onError),
		errorContainer: hex(scheme.errorContainer),
		onErrorContainer: hex(scheme.onErrorContainer),

		// Surface
		surface: hex(scheme.surface),
		onSurface: hex(scheme.onSurface),
		surfaceVariant: hex(scheme.surfaceVariant),
		onSurfaceVariant: hex(scheme.onSurfaceVariant),
		surfaceTint: hex(scheme.surfaceTint),
		surfaceDim: hex(scheme.surfaceDim),
		surfaceBright: hex(scheme.surfaceBright),
		surfaceContainerLowest: hex(scheme.surfaceContainerLowest),
		surfaceContainerLow: hex(scheme.surfaceContainerLow),
		surfaceContainer: hex(scheme.surfaceContainer),
		surfaceContainerHigh: hex(scheme.surfaceContainerHigh),
		surfaceContainerHighest: hex(scheme.surfaceContainerHighest),

		// Inverse
		inverseSurface: hex(scheme.inverseSurface),
		inverseOnSurface: hex(scheme.inverseOnSurface),

		// Background
		background: hex(scheme.background),
		onBackground: hex(scheme.onBackground),

		// Outline
		outline: hex(scheme.outline),
		outlineVariant: hex(scheme.outlineVariant),

		// Utility
		shadow: hex(scheme.shadow),
		scrim: hex(scheme.scrim),
	};
}

/**
 * Apply an MD3 Expressive dynamic color scheme to the document root as CSS
 * custom properties.
 *
 * Sets `--md-sys-color-*` tokens (used by components) and
 * `--color-m3-*` tokens (used by Tailwind). Also sets the `data-theme`
 * attribute for dark mode CSS selectors.
 *
 * @param sourceColorHex - Seed color hex string.
 * @param mode - Light/dark/system mode. Defaults to `"light"`.
 * @param root - Target element. Defaults to `document.documentElement`.
 * @param options - Optional variant + contrast overrides.
 */
export function applyTheme(
	sourceColorHex: string,
	mode: ThemeMode = "light",
	root: HTMLElement = document.documentElement,
	options: GenerateM3ThemeOptions = {},
): void {
	const resolved = resolveMode(mode);
	const colors = generateM3Theme(sourceColorHex, resolved, options);

	for (const [key, value] of Object.entries(colors)) {
		const kebabKey = key.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`);
		root.style.setProperty(`--md-sys-color-${kebabKey}`, value);
		root.style.setProperty(`--color-m3-${kebabKey}`, value);
	}

	// data-theme is always "light" or "dark" — never "system"
	root.setAttribute("data-theme", resolved);
}
